import type { Ref } from 'vue';
import { computed, ref } from 'vue';

import type { ButtonSeverity } from '@components/buttons/types';
import type { ConfirmOptions } from '@modules/core/composables/dialogs/useConfirmDialogs';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import { useI18n } from '@modules/i18n/composables';

import type { IReviewPaymentSystemDetailsButtonsEmitsData } from '../components/VReviewPaymentSystemButtons/types';
import type { IMassReviewPaymentSystemPayload, IReviewPaymentSystemPayload } from '../types';
import { TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS } from '../constants';
import { useMassReviewPaymentSystemMutation, useReviewPaymentSystemMutation } from '../mutations';
import { EReviewPaymentSystemActionType } from '../types';

export interface IConfirmPaymentSystemComposable {
  isSingleConfirmLoading: Ref<boolean>;
  isMassConfirmLoading: Ref<boolean>;
  confirmSingleAction(group: ConfirmOptions['group'], type: EReviewPaymentSystemActionType, payload: IReviewPaymentSystemPayload, event?: MouseEvent): void;
  confirmMassAction(group: ConfirmOptions['group'], type: EReviewPaymentSystemActionType, payload: IMassReviewPaymentSystemPayload, event?: MouseEvent): void;
  onConfirmSingle(data: IReviewPaymentSystemDetailsButtonsEmitsData, actionType: EReviewPaymentSystemActionType): void;
}

export default function useConfirmPaymentSystem(
  rejectionReason: Ref<string | undefined>,
  callback?: () => void,
): IConfirmPaymentSystemComposable {
  const reviewType = ref<EReviewPaymentSystemActionType>(EReviewPaymentSystemActionType.APPROVE);

  const { getTranslation } = useI18n();
  const { confirmEdit } = useConfirmDialogs();

  const { mutateAsync: confirmSingle, isLoading: isSingleConfirmLoading } = useReviewPaymentSystemMutation(rejectionReason);
  const { mutateAsync: confirmMass, isLoading: isMassConfirmLoading } = useMassReviewPaymentSystemMutation(rejectionReason);

  const buttonSeverity = computed<ButtonSeverity>(() => reviewType.value === EReviewPaymentSystemActionType.APPROVE ? 'success' : 'danger');

  function confirmAction(group: ConfirmOptions['group'], accept: () => void | Promise<void>, event?: MouseEvent): void {
    confirmEdit({
      event,
      group,
      acceptProps: {
        severity: buttonSeverity.value,
      },
      message: getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, `${reviewType.value}_MODAL_QUESTION`).value,
      header: getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, `${reviewType.value}_MODAL_TITLE`).value,
      acceptLabel: getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, `${reviewType.value}_MODAL_OK`).value,
      rejectLabel: getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, `${reviewType.value}_MODAL_CANCEL`).value,
      accept() {
        // eslint-disable-next-line ts/no-floating-promises
        accept();
      },
    });
  }

  function confirmSingleAction(group: ConfirmOptions['group'], type: EReviewPaymentSystemActionType, payload: IReviewPaymentSystemPayload, event?: MouseEvent): void {
    reviewType.value = type;
    confirmAction(group, async () => {
      try {
        await confirmSingle(payload);
        callback?.();
      } catch {}
    }, event);
  }

  function confirmMassAction(group: ConfirmOptions['group'], type: EReviewPaymentSystemActionType, payload: IMassReviewPaymentSystemPayload, event?: MouseEvent): void {
    reviewType.value = type;
    confirmAction(group, () => {
      void confirmMass(payload);
    }, event);
  }

  function onConfirmSingle({ id, event }: IReviewPaymentSystemDetailsButtonsEmitsData, actionType: EReviewPaymentSystemActionType): void {
    if (!id)
      return;

    confirmSingleAction(
      actionType === EReviewPaymentSystemActionType.APPROVE ? 'confirmPopup' : 'confirmPopupPaymentSystemReview',
      actionType,
      { id, actionType },
      event,
    );
  }

  return {
    isSingleConfirmLoading,
    isMassConfirmLoading,
    confirmSingleAction,
    confirmMassAction,
    onConfirmSingle,
  };
}
